import { head, prop, propEq, update } from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { listDomains } from 'account/components/userManagement/domains/actions'
import { domainsByIdSelector } from 'account/components/userManagement/domains/selectors'
import {
  listAllDomainTenants,
  listUserTenants,
} from 'account/components/userManagement/tenants/new-actions'
import {
  allDomainTenantsSelector,
  userTenantsSelector,
} from 'account/components/userManagement/tenants/selectors'
import ApiClient from 'api-client/ApiClient'
import { generateApiErrorPayload, getDomainName } from 'api-client/helpers'
import { isSystemUser as isSystemUserFn } from 'app/core/utils/helpers'
import {
  getUserPermissions,
  setupKappiSession,
} from 'app/plugins/account/components/userManagement/users/new-actions'
import { cacheActions } from 'core/caching/cacheReducers'
import useListAction from 'core/hooks/useListAction'
import { permissionsActions } from 'core/session/permissionsReducers'
import type { SessionState } from 'core/session/sessionReducers'
import { sessionActions, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import GlobalDropdownWithSearch, {
  type Item,
  type ItemGroup,
} from '../dropdown/GlobalDropdownWithSearch'

const GlobalTenantDropdown = () => {
  const { keystone } = ApiClient.getInstance()
  const { prefs, updatePrefs } = useScopedPreferences()
  const { currentTenant, currentTenantName, currentTenantSessionToken } = prefs

  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { isSsoToken, isSystemUser, unscopedToken, domain } = session
  const dispatch = useDispatch()

  const { loading: loadingTenants } = useListAction(listUserTenants)
  const tenants = useSelector(userTenantsSelector)

  const { loading: loadingDomainTenants } = useListAction(listAllDomainTenants)
  const domainTenants = useSelector(allDomainTenantsSelector)

  const { loading: loadingDomains } = useListAction(listDomains)
  const domainsById = useSelector(domainsByIdSelector)

  const dropdownTenants = useMemo(() => {
    return isSystemUser ? domainTenants : tenants
  }, [tenants, domainTenants, isSystemUser])

  // Dropdown options
  const dropdownOptions: Item[] = useMemo(() => {
    return dropdownTenants.map((tenant) => ({
      label: tenant.name,
      value: tenant.id,
    }))
  }, [dropdownTenants])

  const groupedDropdownOptions: ItemGroup[] = useMemo(() => {
    return dropdownTenants
      .reduce((accum, tenant) => {
        const accumIndex = accum.findIndex(
          (group) =>
            group.label === domainsById[tenant.domain_id]?.name || group.label === tenant.domain_id,
        )
        if (accumIndex >= 0) {
          return update(
            accumIndex,
            {
              ...accum[accumIndex],
              items: [
                ...accum[accumIndex].items,
                {
                  label: tenant.name,
                  value: tenant.id,
                },
              ],
            },
            accum,
          )
        } else {
          return [
            ...accum,
            {
              label: domainsById[tenant.domain_id]?.name || tenant.domain_id,
              items: [
                {
                  label: tenant.name,
                  value: tenant.id,
                },
              ],
            },
          ]
        }
      }, [])
      .map((group) => ({
        ...group,
        label: `Domain: ${group.label}`,
      }))
  }, [dropdownTenants, domainsById])

  // Set currentTenant in prefs on first-ever load
  useEffect(() => {
    // If tenants are not loaded, do nothing
    if (!dropdownTenants?.length) {
      return
    }

    // For system users (platform_admin / sp_admin): a saved tenant is valid if it's a
    // mid-session switch (same unscopedToken) OR it belongs to the user's login domain.
    // A new token means a new login — cross-domain prefs are reset; same-domain prefs
    // are preserved (PCD-9529). Mid-session switches stamp the token so they survive
    // dropdownTenants refreshes (e.g. after clearCache).
    // For regular users, tenants are domain-scoped by the API — valid only if the saved
    // tenant ID still exists in the list (handles revoked access / deleted tenants).
    const isNewSession = unscopedToken !== currentTenantSessionToken
    const currentIsValid = isSystemUser
      ? !isNewSession ||
        dropdownTenants?.some((t) => t?.id === currentTenant && t.domain_id === domain?.id)
      : dropdownTenants?.some((t) => t?.id === currentTenant)

    if (!currentTenant || !currentTenantName || !currentIsValid) {
      const userDomainTenants = domain?.id
        ? dropdownTenants.filter((t) => t.domain_id === domain.id)
        : dropdownTenants
      const pool = userDomainTenants.length ? userDomainTenants : dropdownTenants
      const defaultTenant = pool.find(propEq('name', 'service')) || head(pool)
      updatePrefs({
        currentTenant: defaultTenant?.id,
        currentTenantName: defaultTenant?.name,
        currentTenantSessionToken: unscopedToken,
      })
    }
    // Intentional: deps limited to dropdownTenants. Other vars (isSystemUser, domain,
    // unscopedToken, currentTenant*) are stable across re-renders within a session;
    // adding them would re-trigger selection on every Redux session update, breaking
    // mid-session cross-domain tenant switches (PCD-9529).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownTenants])

  const updateCurrentTenant = async (tenant: Item) => {
    // const tenant = tenants?.find(propEq('name', tenantName))
    if (!tenant?.value || !tenant?.label) {
      return
    }

    const { user, role, scopedToken } = await keystone.changeProjectScopeWithToken(
      tenant.value,
      isSsoToken,
    )

    // Find the tenant in tenants list
    const matchedTenant = tenants?.find(propEq('id', tenant.value))

    // Refetch dex token
    await setupKappiSession({
      username: '',
      password: unscopedToken,
      domain: getDomainName(),
      activeTenant: matchedTenant,
    })

    dispatch(cacheActions.clearCache())
    dispatch(sessionActions.resetSession({})) // Reset session params when tenant changes

    if (isSystemUserFn()) {
      // Same system token is used across all tenants/domains, do not refresh
      // the token
      dispatch(
        sessionActions.updateSession({
          userDetails: { ...user, role },
          activeTenant: tenant.label,
          vmsSearchParams: null,
        }),
      )
      return
    } else {
      dispatch(
        sessionActions.updateSession({
          userDetails: { ...user, role },
          scopedToken,
          activeTenant: tenant.label,
          vmsSearchParams: null,
        }),
      )
    }

    // Permissions are per-tenant/domain; clear the stale map before refetching so
    // permission-gated UI fails closed instead of showing the previous tenant's grants.
    dispatch(permissionsActions.clearPermissions())
    try {
      const permissions = await getUserPermissions()
      dispatch(permissionsActions.updatePermissions(permissions))
    } catch (err) {
      console.error(
        'Failed to refresh permissions after tenant switch',
        generateApiErrorPayload(err).message,
      )
      dispatch(permissionsActions.clearPermissions())
    }

    // Create Appication Credential for tenant-user
    // if (isKaapiEnabled() && isKaapiTokenAvailable()) {
    //   createKaapiApplicationCredentials(ApplicationCredentialType.IdentityRef)
    // }
  }

  const handleTenantSelect = useCallback(
    async (newTenant: Item) => {
      if (!newTenant?.value || !newTenant?.label) {
        return
      }

      // Set loadingTenant to true
      dispatch(
        sessionActions.updateSession({
          loadingTenant: true,
        }),
      )

      await updateCurrentTenant(newTenant)
      updatePrefs({
        currentTenant: newTenant?.value,
        currentTenantName: newTenant?.label,
        currentTenantSessionToken: unscopedToken,
        allTenants: false, //Reset AllTenants to false when tenant is changed
      })

      // Set loadingTenant to false
      dispatch(
        sessionActions.updateSession({
          loadingTenant: false,
        }),
      )
    },
    [tenants],
  )

  return (
    <GlobalDropdownWithSearch
      label="Tenant"
      items={dropdownOptions}
      groupedItems={isSystemUser ? groupedDropdownOptions : null}
      value={String(currentTenant)}
      onChange={handleTenantSelect}
      loading={loadingTenants || loadingDomainTenants || loadingDomains}
    />
  )
}

export default GlobalTenantDropdown
